import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SiteProvider } from "@/contexts/SiteContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import ArticlesPage from "@/pages/ArticlesPage";
import ArticleGeneratorPage from "@/pages/ArticleGeneratorPage";
import KeywordsPage from "@/pages/KeywordsPage";
import CalendarPage from "@/pages/CalendarPage";
import BacklinksPage from "@/pages/BacklinksPage";
import BacklinkAutomationPage from "@/pages/BacklinkAutomationPage";
import Web2Page from "@/pages/Web2Page";
import WordPressPage from "@/pages/WordPressPage";
import SettingsPage from "@/pages/SettingsPage";
import ApiKeysPage from "@/pages/ApiKeysPage";
import AutomationPage from "@/pages/AutomationPage";
import MonitorPage from "@/pages/MonitorPage";
import AuditSitePage from "@/pages/AuditSitePage";
import BusinessAnalysisPage from "@/pages/BusinessAnalysisPage";
import IndexingPage from "@/pages/IndexingPage";
import ContentCalendarPage from "@/pages/ContentCalendarPage";
import PageSpeedPage from "@/pages/PageSpeedPage";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse-glow w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
          <div className="w-6 h-6 rounded-full bg-primary"></div>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse-glow w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
          <div className="w-6 h-6 rounded-full bg-primary"></div>
        </div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <SiteProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={
              <PublicRoute><LoginPage /></PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute><RegisterPage /></PublicRoute>
            } />
            <Route path="/" element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="articles" element={<ArticlesPage />} />
              <Route path="articles/new" element={<ArticleGeneratorPage />} />
              <Route path="keywords" element={<KeywordsPage />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="backlinks" element={<BacklinksPage />} />
              <Route path="backlinks/automation" element={<BacklinkAutomationPage />} />
              <Route path="web2" element={<Web2Page />} />
              <Route path="wordpress" element={<WordPressPage />} />
              <Route path="automation" element={<AutomationPage />} />
              <Route path="content-calendar" element={<ContentCalendarPage />} />
              <Route path="monitor" element={<MonitorPage />} />
              <Route path="technical-audit" element={<Navigate to="/audit-site?tab=seo" replace />} />
              <Route path="geo-audit" element={<Navigate to="/audit-site?tab=geo" replace />} />
              <Route path="audit-site" element={<AuditSitePage />} />
              <Route path="business-analysis" element={<BusinessAnalysisPage />} />
              <Route path="indexing" element={<IndexingPage />} />
              <Route path="pagespeed" element={<PageSpeedPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="api-keys" element={<ApiKeysPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" />
      </SiteProvider>
    </AuthProvider>
  );
}

export default App;
